import { computed, toValue } from 'vue';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/vue-query';
import { useSearchAwareParams } from '@/composables/useSearchAwareParams.js';
import { useTransferRecipientIdentifiersStore } from '@/stores/transferRecipients.js';

import api from './api';

export const useCreateTransferRecipient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency'] });
      queryClient.invalidateQueries({ queryKey: ['agencies'] });
      queryClient.invalidateQueries({ queryKey: ['agency-identifiers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipients'] });
    },
  });
};

export const useUpdateTransferRecipient = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.update(toValue(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency'] });
      queryClient.invalidateQueries({ queryKey: ['agencies'] });
      queryClient.invalidateQueries({ queryKey: ['provider-entity'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipient'] });
      queryClient.invalidateQueries({ queryKey: ['agency-identifiers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipient-identifiers'] });
    },
  });
};

export const useTransferRecipientById = (id) => {
  const enabled = computed(() => !!toValue(id));
  return useQuery({
    queryKey: ['transfer-recipient', id],
    queryFn: () => api.getById(toValue(id)),
    enabled,
  });
};

export const useProviderEntityByTransferRecipientId = (recipientId) => {
  const enabled = computed(() => !!toValue(recipientId));
  return useQuery({
    queryKey: ['provider-entity', recipientId],
    queryFn: () => api.getProviderEntityById(toValue(recipientId)),
    enabled,
  });
};

export const useTransferRecipientsByAgency = (agencyId, params = {}) => {
  const enabled = computed(() => !!agencyId);
  return useQuery({
    queryKey: ['transfer-recipients', agencyId, params],
    queryFn: () => api.getAllByAgency(toValue(agencyId), toValue(params)),
    enabled,
  });
};

export const useSaveTransferRecipientIdentifiers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.saveIdentifiers(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agency'] });
      queryClient.invalidateQueries({ queryKey: ['agencies'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipient'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['agency-identifiers'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-recipient-identifiers'] });
    },
  });
};

export const useInfiniteIdentifiersByTransferRecipientId = (id) => {
  const { filters } = useTransferRecipientIdentifiersStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
      sort_order: 'desc',
    },
  });

  return useInfiniteQuery({
    queryKey: ['transfer-recipient-identifiers', searchParams, id],
    enabled: computed(() => !!toValue(id)),
    queryFn: async ({ pageParam = 1 }) =>
      await api.getIdentifiers(toValue(id), { ...searchParams.value, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.meta?.pagination;
      if (!pagination) return undefined;
      const { page, total_pages } = pagination;
      return page < total_pages ? page + 1 : undefined;
    },
  });
};

export const useTransferRecipientIdentifiers = (id) => {
  const { filters } = useTransferRecipientIdentifiersStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
      sort_order: 'desc',
    },
  });
  return useQuery({
    queryKey: ['transfer-recipient-identifiers', id, searchParams],
    queryFn: () => api.getIdentifiers(toValue(id), toValue(searchParams)),
    enabled: computed(() => !!toValue(id)),
  });
};
